import { MercureService, RouteEntitySubscriberService } from '../../services';
import { Entity } from '../../models/entities/Entity';
import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import { ActivatedRouteSnapshot, CanDeactivate, RouterStateSnapshot } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { isSignedOutUrl } from '../../utils/signed-out-utils/signed-out-utils';
import { afterLeaving, confirmLeavingUnsavedChanges } from '../unsavedChangesGuard/unsavedChangesGuard';

@Injectable({ providedIn: 'root' })
export class ReleaseListGuard<T extends Entity> implements CanDeactivate<unknown> {
  private mercureService = inject<MercureService<T>>(MercureService);
  private tracker = inject(RouteEntitySubscriberService);
  #injector = inject(Injector);

  canDeactivate(
    _component: unknown,
    _currentRoute: ActivatedRouteSnapshot,
    _currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot,
  ): Observable<boolean> | boolean {
    // The session is gone: the release call cannot get a token, and its failure would send the app out again, endlessly.
    if (nextState && isSignedOutUrl(nextState.url)) return true;
    // Staying on a form with unsaved changes keeps the lock: ask before releasing it. The router calls a class guard's
    // method outside an injection context, and asking injects, so it runs in this guard's.
    const leave = nextState
      ? runInInjectionContext(this.#injector, () => confirmLeavingUnsavedChanges(nextState.url))
      : true;
    return afterLeaving(leave, () => this.#release());
  }

  #release(): Observable<boolean> {
    return this.tracker.releaseActiveIfOwned().pipe(
      tap(() => {
        // also remove this tab from localStorage so releaseActiveIfOwned can see "last tab"
        const activeKey = this.tracker.getCurrentActiveSignalKey();
        if (activeKey) {
          const entity = this.tracker.getSignal(activeKey)();
          if (entity && entity['@id']) {
            this.tracker.unregisterTab(entity['@id']!);
          }
        }
      }),
      map(() => {
        this.mercureService.unsubscribeFromAllEntities();
      }),
      map(() => true),
    );
  }
}
